import api from "../../api";

/**
 * Assessment API Service
 * Handles all API calls for the new Assessment workspace
 */

/**
 * Create a new code execution run
 * @param {number} topicId - Course topic ID
 * @param {number} problemId - Programming problem ID
 * @param {Object} payload - { submission_id?, code?, language, is_custom_input, custom_input? }
 * @returns {Promise<{run_id, state, total_cases, completed_cases, submission_id}>}
 */
export const createRun = async (topicId, problemId, payload) => {
  const response = await api.post(
    `api/assessment/${topicId}/problems/${problemId}/runs/`,
    payload
  );
  return response.data;
};

/**
 * Get run status with progress
 * @param {number} topicId - Course topic ID
 * @param {string} runId - UUID of the run
 * @returns {Promise<{run_id, state, total_cases, completed_cases, passed_cases, score, max_score, test_case_results?, error_message?}>}
 */
export const getRunStatus = async (topicId, runId) => {
  const response = await api.get(`api/assessment/${topicId}/runs/${runId}/`);
  return response.data;
};

/**
 * Cancel a running execution
 * @param {number} topicId - Course topic ID
 * @param {string} runId - UUID of the run
 * @returns {Promise<{run_id, state, message}>}
 */
export const cancelRun = async (topicId, runId) => {
  const response = await api.post(
    `api/assessment/${topicId}/runs/${runId}/cancel/`
  );
  return response.data;
};

/**
 * Get submission history for a problem
 * @param {number} topicId - Course topic ID
 * @param {number} problemId - Programming problem ID
 * @returns {Promise<{submissions, best_score, total_attempts}>}
 */
export const getSubmissionHistory = async (topicId, problemId) => {
  const response = await api.get(
    `api/assessment/${topicId}/problems/${problemId}/submissions/`
  );
  return response.data;
};

/**
 * Restore a previous submission's code
 * @param {number} topicId - Course topic ID
 * @param {number} problemId - Programming problem ID
 * @param {number} submissionId - Submission ID to restore
 * @returns {Promise<{id, code, language, timestamp}>}
 */
export const restoreSubmission = async (topicId, problemId, submissionId) => {
  const response = await api.post(
    `api/assessment/${topicId}/problems/${problemId}/submissions/${submissionId}/restore/`
  );
  return response.data;
};

/**
 * Save code as a draft (existing API)
 * @param {number} topicId - Course topic ID
 * @param {number} problemId - Programming problem ID
 * @param {Object} payload - { submission, language }
 * @returns {Promise<{id, ...}>}
 */
export const saveSubmission = async (topicId, problemId, payload) => {
  const response = await api.post(`course/${topicId}/p/${problemId}/`, payload);
  return response.data;
};

/**
 * Submit the final solution: locks graded runs and returns the reference solution
 * @returns {Promise<{final, answer_key: {solution}}>}
 */
export const finalizeProblem = async (topicId, problemId) => {
  const response = await api.post(`api/assessment/${topicId}/problems/${problemId}/finalize/`);
  return response.data;
};

/**
 * Get programming problems for a topic (existing API)
 * @param {number} topicId - Course topic ID
 * @returns {Promise<Array>}
 */
export const getProblems = async (topicId) => {
  const response = await api.get(`course/${topicId}/p/`);
  return response.data;
};

/**
 * Poll for run status until completion
 * @param {number} topicId - Course topic ID
 * @param {string} runId - UUID of the run
 * @param {Function} onProgress - Callback for progress updates
 * @param {number} interval - Polling interval in ms (default 2000)
 * @param {number} timeout - Max timeout in ms (default 300000 = 5min)
 * @param {AbortSignal} signal - Optional AbortSignal for cancellation
 * @returns {Promise<Object>} Final run status
 */
export const pollRunStatus = async (
  topicId,
  runId,
  onProgress,
  interval = 2000,
  timeout = 300000,
  signal = null
) => {
  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
    };

    // Handle abort
    if (signal) {
      signal.addEventListener("abort", () => {
        cleanup();
        reject(new Error("Polling aborted"));
      });
    }

    const poll = async () => {
      // Check if aborted
      if (signal?.aborted) {
        cleanup();
        reject(new Error("Polling aborted"));
        return;
      }

      try {
        const status = await getRunStatus(topicId, runId);
        onProgress?.(status);

        // Check if done
        if (["done", "error", "cancelled"].includes(status.state)) {
          cleanup();
          resolve(status);
          return;
        }

        // Check timeout
        if (Date.now() - startTime > timeout) {
          cleanup();
          reject(new Error("Polling timeout"));
          return;
        }

        // Continue polling
        timeoutId = setTimeout(poll, interval);
      } catch (error) {
        cleanup();
        reject(error);
      }
    };

    poll();
  });
};

export default {
  createRun,
  finalizeProblem,
  getRunStatus,
  cancelRun,
  getSubmissionHistory,
  restoreSubmission,
  saveSubmission,
  getProblems,
  pollRunStatus,
};
